import React, { useState, useEffect } from 'react';
import MentionBox from '../components/MentionBox';
import StickerPicker from '../components/StickerPicker';
import { t } from '../i18n';
import { useNavigate } from 'react-router-dom';

function WritePost({ user }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [communities, setCommunities] = useState([]);
  const [selectedCommunity, setSelectedCommunity] = useState('');
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    fetch('/api/communities/', {
      headers: { 'Authorization': `Token ${user.token}` }
    }).then(r => r.json()).then(d => setCommunities(Array.isArray(d) ? d : [])).catch(() => {});
  }, [user]);

  if (!user) {
    return (
      <div style={styles.container}>
        <div style={styles.loginRequired}>
          <h2>{t('wp_login_required')}</h2>
          <p>{t('wp_login_body')}</p>
        </div>
      </div>
    );
  }

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const insertTag = (before, after) => {
    const textarea = document.getElementById('post-body');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = body.substring(start, end);
    const newText = body.substring(0, start) + before + selected + after + body.substring(end);
    setBody(newText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selected.length);
    }, 0);
  };

  const handleSubmit = async () => {
    if (!title || !body) {
      alert(t('wp_fill_all'));
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('body', body);
      if (image) formData.append('image', image);

      const response = await fetch('/api/posts/', {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}` },
        body: formData
      });
      if (response.ok) {
        navigate('/');
      } else {
        const errData = await response.json();
        const msg = errData?.error || errData?.title?.[0] || errData?.body?.[0] || 'Failed to publish post';
        const isban = msg.toLowerCase().includes('banned') || msg.toLowerCase().includes('rejected');
        if (isban) {
          const word = msg.split(': ')[1] || 'inappropriate content';
          alert('Post Rejected!\n\nBanned word detected: "' + word + '"\n\nPlease remove it and try again.');
        } else {
          alert('Error: ' + msg);
        }
      }
    } catch (err) {
      console.error(err);
      alert(t('wp_network_error'));
    }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <h2 style={styles.heading}>✍️ Write a Post</h2>
          <a href="/" style={{ backgroundColor:'#f3e5f5', color:'#6200ea', fontSize:'13px', textDecoration:'none', fontWeight:'bold', padding:'8px 14px', borderRadius:'15px' }}>📰 View Posts</a>
        </div>
        <input
          style={styles.input}
          placeholder={t('wp_title_ph')}
          value={title}
          onChange={e => setTitle(e.target.value)}
        />
        <div style={{display:'flex',gap:'4px',flexWrap:'wrap',padding:'8px',backgroundColor:'#f3e5f5',borderRadius:'10px 10px 0 0',border:'2px solid #ddd',borderBottom:'none'}}>
          {[{l:'B',a:()=>insertTag('**','**')},{l:'I',a:()=>insertTag('*','*')},{l:'H1',a:()=>insertTag('# ','')},{l:'H2',a:()=>insertTag('## ','')},{l:'❝',a:()=>insertTag('> ','')},{l:'Code',a:()=>insertTag('CODE','')},{l:'🔗',a:()=>insertTag('[','](url)')},{l:'List',a:()=>insertTag('\n- ','')},{l:'Line',a:()=>insertTag('\n---\n','')}].map((btn,i)=>(
            <button key={i} onClick={btn.a} style={{padding:'4px 10px',borderRadius:'6px',border:'1px solid #ccc',backgroundColor:'white',cursor:'pointer',fontSize:'13px'}}>{btn.l}</button>
          ))}
        </div>
        <MentionBox
          inputId="post-body"
          multiline
          allowSpecials
          style={styles.textarea || styles.body || { width: '100%', minHeight: 220, padding: 12, fontSize: 15, lineHeight: 1.6, border: '0.5px solid #B4B2A9', borderRadius: 8, fontFamily: 'inherit', resize: 'vertical' }}
          placeholder={t('wp_body_ph') || 'Write your post... use @ to mention'}
          value={body}
          onChange={setBody}
        />
        <div style={{ marginTop: 6 }}>
          <StickerPicker onPick={(k) => setBody((b) => `${b}[[sticker:${k}]]`)} />
        </div>
        <div style={styles.imageRow}>
          <label style={styles.imageLabel}>
            Add Image
            <input type="file" accept="image/*" onChange={handleImageChange} style={styles.fileInput} />
          </label>
          {preview && <img src={preview} alt="preview" style={styles.preview} />}
        </div>
        {communities.length > 0 && (
          <select style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '2px solid #e0e0e0', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box', backgroundColor: 'white' }}
            value={selectedCommunity} onChange={e => setSelectedCommunity(e.target.value)}>
            <option value="">🌐 Add to Community (optional)</option>
            {communities.map(c => <option key={c.slug} value={c.slug}>{c.icon} {c.name}</option>)}
          </select>
        )}
        <button style={styles.btn} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Publishing...' : 'Publish Post'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: '800px', margin: '0 auto', padding: '20px' },
  card: { backgroundColor: 'white', borderRadius: '10px', padding: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  heading: { color: '#6200ea', marginBottom: '20px' },
  input: { width: '100%', padding: '12px', fontSize: '18px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '15px', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '12px', fontSize: '16px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '15px', boxSizing: 'border-box', lineHeight: '1.6' },
  imageRow: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' },
  imageLabel: { backgroundColor: '#f3e5f5', color: '#6200ea', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' },
  fileInput: { display: 'none' },
  preview: { maxWidth: '100px', maxHeight: '100px', borderRadius: '8px', objectFit: 'cover' },
  btn: { backgroundColor: '#6200ea', color: 'white', border: 'none', padding: '12px 30px', borderRadius: '20px', fontSize: '16px', cursor: 'pointer', fontWeight: 'bold' },
  loginRequired: { textAlign: 'center', padding: '50px', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
};

export default WritePost;
